import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiZap, FiMap, FiAward, FiEye, FiTarget, FiExternalLink } from 'react-icons/fi';
import { FaSkull } from 'react-icons/fa';
import StoreButtons from '../components/StoreButtons';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-purple/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-purple/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-purple" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const STRATA = [
  { name: 'I · The Coast', lord: 'The Harbinger', guardian: 'The Bell-Warden' },
  { name: 'II · The Abyss', lord: 'The Devourer', guardian: 'The Silt Mother' },
  { name: 'III · The Sunken City', lord: 'The Architect', guardian: 'The Coral Cantor' },
  { name: 'IV · The Rising', lord: 'The Ancient One', guardian: 'The Hollow Leviathan' },
  { name: 'V · The Void', lord: 'The Void Titan', guardian: 'The Void Lamprey' },
];

const EldritchDescentX = () => {
  const PRODUCT = getProduct('eldritch-descent-x');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-purple/10 border border-accent-purple/20 text-accent-purple font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FaSkull} /> Cosmic-horror shoot 'em up · 1-2 players
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Eldritch Descent <span className="text-accent-purple">X</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "The more power you take, the more the game lies to you."
            </p>
            <div className="flex flex-col items-center justify-center gap-5">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-accent-purple hover:bg-accent-purple/90" className="justify-center" />
              <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-white transition-colors">
                eldritchx.mediaverselabs.com <SafeIcon icon={FiExternalLink} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pitch */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            A vertical shoot 'em up of cosmic horror. Dive your lamp-lit ship through five drowned strata, from the
            coast to the bottom of the ocean and beyond the sky. Take corruption from the Old Ones for power, and as
            your sanity slips the voices change, the dark closes in and the game stops telling you the truth. The
            successor to Eldritch Descent, rebuilt from scratch with painted art, a voiced story and two-pilot co-op.
          </p>
        </div>
      </section>

      {/* Trailer */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Trailer — Eldritch Descent X</p>
          <div className="border border-border bg-black">
            <video controls preload="metadata" playsInline poster="/products/eldritch-descent-x.webp" className="w-full block">
              <source src="https://eldritchx.mediaverselabs.com/assets/trailer.mp4" type="video/mp4" />
            </video>
          </div>
          <p className="text-xs text-muted mt-3">All footage is captured in-engine.</p>
        </div>
      </section>

      {/* Reel */}
      <section className="pb-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Eldritch Descent X</p>
          <Reel frames={PRODUCT.frames} label="Eldritch Descent X" sound={false} ratio="aspect-video" />
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">Pure arcade fire, readable bullets and deep scoring, in a world that should stay asleep.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiMap} title="Five Drowned Strata" desc="Three levels each, a guardian halfway down and a lord at the bottom. Seven lords in all, two of them hidden." />
            <FeatureCard icon={FiEye} title="Sanity That Lies" desc="Take corruption for power. As your mind slips, phantoms join the swarm, the HUD glitches and lies, and the voices whisper things they should not." />
            <FeatureCard icon={FiTarget} title="Horror Never Hides a Bullet" desc="Enemy fire is always magenta or cyan and drawn on top of everything. The dread is in the world, never in a shot you could not see." />
            <FeatureCard icon={FiZap} title="Medallions and Boons" desc="Eleven secondary weapons that rank up to III, plus Overdrive, the Lodestone and the Tide Mirror, which throws bullets back." />
            <FeatureCard icon={FiUsers} title="1-2 Player Co-op" desc="Two pilots on one screen with shared lives, their own power and shield, and a downed partner you fly over to revive." />
            <FeatureCard icon={FiAward} title="Made to Be Replayed" desc="Grazes, chains, gold shards and sea mines for score. Four difficulties, a daily descent, four endings and a narrated Codex." />
          </div>
        </div>
      </section>

      {/* Strata + specs */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="space-y-6">
              <h2 className="text-4xl font-black text-white uppercase italic">The <span className="text-accent-purple">Descent</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {STRATA.map((s) => (
                  <div key={s.name} className="p-4 bg-card border border-border rounded-md">
                    <h4 className="text-white font-bold mb-1">{s.name}</h4>
                    <p className="text-xs text-muted">Guardian: {s.guardian} · Lord: {s.lord}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-none p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase">Details</h3>
              <div className="space-y-4 text-sm">
                {[
                  ['Platform', 'Windows 10 / 11'],
                  ['Players', '1-2 local co-op'],
                  ['Controls', 'Keyboard and gamepad, fully remappable'],
                  ['Languages', 'English, Spanish, French, German, Portuguese, Italian'],
                  ['Voices', 'English and Spanish'],
                  ['Status', 'Coming soon to Steam, Epic and Microsoft Store'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-2 border-b border-border/50">
                    <span className="text-muted">{k}</span>
                    <span className="text-white text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <StoreButtons stores={PRODUCT.stores} accentColor="bg-accent-purple hover:bg-accent-purple/90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-purple/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FaSkull} className="w-16 h-16 text-accent-purple mx-auto mb-8" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">How deep can you descend?</h2>
          <p className="text-muted mb-10 text-lg">
            One-time purchase. No microtransactions. No online requirement. Coming soon to PC.
          </p>
          <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
             className="btn-primary bg-accent-purple hover:bg-accent-purple/90 text-white px-10 py-4 inline-flex decoration-transparent">
            Visit the Eldritch Descent X site
          </a>
        </div>
      </section>
    </div>
  );
};

export default EldritchDescentX;
